<template>
  <JoszakiPage
    :page-name="pageName"
    :tracking-params="trackingParams"
    class="InspirationPicturePage"
    inner-class="flex flex-col min-w-0"
    :loading="$fetchState.pending || !selectedPhoto"
    :error="$fetchState.error"
  >
    <div
      class="fixed z-50 bg-white border-gray-300 drop-shadow rounded-full max-md:mt-3 max-md:ml-3 p-3 hover:bg-gray-100 transition-all duration-300 cursor-pointer"
      @click="onBackButtonClick"
    >
      <IconComponent icon="angle-left" class="w-5 h-5" />
    </div>

    <video
      v-if="selectedPhoto.videoFileName"
      ref="videoPlayer"
      v-lazy-load :data-src="$helpers.getInspirationVideo(selectedPhoto.videoFileName)"
      class="object-contain w-full h-[500px] md:h-[600px] block"
      muted
      loop
      playsinline
      data-not-lazy
    />
    <img
      v-else
      v-lazy-load :data-src="$helpers.getReferencePhoto(selectedPhoto.photo?.name ?? '')"
      :alt="`${selectedPhoto.title} - ${selectedPhoto.description}`"
      class="object-contain w-full h-[500px] md:h-[600px] block"
      fetchpriority="high"
      data-not-lazy
      @error="$helpers.noImage"
    />

    <JoszakiHeading>{{ selectedPhoto.title }}</JoszakiHeading>

    <NuxtLink
      v-if="selectedPhoto.photo?.profession && $branding.isBerufExperten"
      :to="
        localePath({
          name: 'professionPage',
          params: { profession: selectedPhoto.photo.profession.seoName },
        })
      "
      class="!text-inherit pb-2"
    >
      <span class="flex flex-row gap-1 items-center">
        <IconComponent icon="briefcase" class="min-w-[16px]" />
        <span class="min-w-0 truncate">
          {{ selectedPhoto.photo.profession.name ?? "" }}
        </span>
      </span>
    </NuxtLink>
    <NuxtLink
      v-if="selectedPhoto?.photo?.person"
      :to="
        localePath({
          name: 'professionalPage',
          params: { professionalSeoName: selectedPhoto.photo.person.seoName },
        })
      "
      class="flex flex-row items-center gap-3 min-w-0 !text-inherit"
    >
      <img
        v-lazy-load :data-src="
          $helpers.getProfilePictureThumbnailUrl(
            selectedPhoto.photo.person.profilePhotoId
          )
        "
        class="rounded-full w-14 h-14"
        alt=""
        @error="$helpers.noImage"
      />
      <span class="flex flex-col flex-1 min-w-0">
        <span class="truncate flex-1">
          {{ selectedPhoto.photo.person.name }}
        </span>
        <span
          v-if="selectedPhoto.photo.person.professions.length"
          class="flex flex-row gap-1 items-center"
        >
          <IconComponent icon="briefcase" class="min-w-[16px]" />
          <span class="min-w-0 truncate">
            {{
              selectedPhoto.photo.person.professions
                .map((p) => p.name)
                .join(", ")
            }}
          </span>
        </span>
      </span>
    </NuxtLink>

    <hr />

    <BlockTender
      text-input
      :title="$t('inspirations.blockTenderTitle')"
      :sub-title="$t('inspirations.blockTenderSubTitle')"
      @requestTender="
        onRequestTender({
          source: 'inspiration-picture-page',
          description: $event,
        })
      "
    />

    <hr />
    <h2 class="text-center pb-5 font-bold">
      {{ $t("inspirations.moreInspirations") }}
    </h2>

    <div class="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-3 mb-3">
      <template v-for="photo in inspiration.photos">
        <InspirationMediaItem
          v-if="photo.seoName !== selectedPhoto.seoName"
          :key="photo.id"
          :media="photo"
          :inspiration="inspiration"
        />
      </template>
    </div>

    <div
      v-if="relatedInspirations.length > 0"
      class="flex flex-col gap-2 border-t border-t-gray-300 pt-3"
    >
      <NuxtLink
        v-for="(relatedInspiration, index) in relatedInspirations"
        :key="index"
        :to="relatedInspiration.url"
      >
        {{ relatedInspiration.pageTitle }}
      </NuxtLink>
    </div>
  </JoszakiPage>
</template>

<router>
{
name: 'inspirationPicturePage'
}
</router>

<script>
import { useRoute, useContext, useFetch } from "@nuxtjs/composition-api";
import { ref } from "vue";
import { useRequestTender } from "~/composables/requestTender";
import JoszakiPage from "~/components/_refactored/common/JoszakiPage.vue";
import useRestClient from "~/helpers/restClient";
import BlockTender from "~/components/_refactored/blocks/BlockTender.vue";
import InspirationMediaItem from "~/components/_refactored/inspirationPage/InspirationMediaItem/InspirationMediaItem.vue";
import { useFetchRelatedInspirations } from "~/components/_refactored/inspirationPage/useFetchRelatedInspirations";
import { EVENT_PAGE_NAMES } from "~/data/pageNames";
import INSPIRATION_QUERY from "~/components/_refactored/inspirationPage/inspirationBySeoName.graphql";
import pages from "~/config/pages";

export default {
  name: "InspirationPicturePage",
  components: {
    JoszakiPage,
    BlockTender,
    InspirationMediaItem,
  },
  layout: "DefaultLayout",
  setup() {
    const { $api, redirect, i18n, error, $query } = useContext();
    const route = useRoute();
    const restClient = useRestClient($api);
    const fetchRelatedInspirations = useFetchRelatedInspirations();

    const path = route.value.path;
    const { inspirationSeoName, pictureSeoName } = route.value.params;

    const inspiration = ref({});
    const selectedPhoto = ref({});
    const relatedInspirations = ref([]);

    const trackingParams = {
      inspirationSeoName,
      pictureSeoName,
    };

    useFetch(async () => {
      const [responseRedirect, responseInspiration] = await Promise.all([
        restClient.redirect.get(path),
        $query(INSPIRATION_QUERY, {
          seoName: inspirationSeoName,
        }),
      ]);

      if (responseRedirect) {
        redirect(301, redirect);
      }

      if (!responseInspiration.inspirationBySeoName) {
        error({
          statusCode: 404,
          message: i18n.t("error.codes.ENTITY_NOT_FOUND"),
        });
        return;
      }

      inspiration.value = responseInspiration.inspirationBySeoName;
      selectedPhoto.value = (
        responseInspiration.inspirationBySeoName?.photos ?? []
      ).find((photo) => photo.seoName === pictureSeoName);

      if (inspiration.value) {
        relatedInspirations.value = await fetchRelatedInspirations(
          inspiration.value
        );
      }

      if (!selectedPhoto.value) {
        error({
          statusCode: 404,
          message: i18n.t("error.codes.ENTITY_NOT_FOUND"),
        });
      }
    });

    return {
      inspiration,
      selectedPhoto,
      trackingParams,
      relatedInspirations,
      pageName: EVENT_PAGE_NAMES.INSPIRATION_PICTURE_PAGE,
      ...useRequestTender(),
    };
  },
  head() {
    const title = this.selectedPhoto
      ? this.$t("inspirations.photoTitle", {
          photoTitle: this.selectedPhoto?.title,
          inspirationTitle: this.$helpers.capitalize(
            this.inspiration?.pageTitle ?? ""
          ),
        })
      : "";
    const head = {
      title,
      meta: [
        {
          hid: "og:title",
          property: "og:title",
          content: title,
        },
        {
          hid: "og:url",
          property: "og:url",
          content: `${this.$helpers.topDomain}${this.$route.fullPath}`,
        },
        {
          hid: "og:image",
          property: "og:image",
          content: this.selectedPhoto?.photoSrc ?? "",
        },
        {
          hid: "description",
          name: "description",
          content: this.selectedPhoto?.description ?? "",
        },
        {
          hid: "og:description",
          property: "og:description",
          content: this.selectedPhoto?.description ?? "",
        },
      ],
      link: [
        {
          rel: "canonical",
          href: `${this.$helpers.topDomain}${this.$route.path}`,
        },
      ],
    };
    const pageInstance = pages.inspirationPicturePage.resolveUrl({
      inspirationSeoName: {
        seoName: this.$route.params.inspirationSeoName,
      },
      pictureSeoName: {
        seoName: this.$route.params.pictureSeoName,
      },
    });

    const isNoIndex = pageInstance.isNoIndex;
    if (isNoIndex) {
      head.meta.push({
        hid: "robots",
        name: "robots",
        content: "noindex",
      });
    }

    return head;
  },
  mounted() {
    setTimeout(() => {
      const videoElement = this.$refs.videoPlayer;
      if (videoElement) {
        const observer = new IntersectionObserver(
          (entries) => {
            entries.forEach((entry) => {
              if (entry.isIntersecting) {
                return videoElement.play();
              }

              videoElement.pause();
            });
          },
          { threshold: 0.5 }
        );

        observer.observe(videoElement);
      }
    }, 300);
  },
  methods: {
    onRequestTender({ source, description }) {
      this.requestTender({
        source,
        description,
      });
    },
    onBackButtonClick() {
      console.info(
        "Back button clicked",
        window.history,
        window.history.length
      );
      if (!window.history || window.history.length <= 1) {
        this.$router.push({
          name: "inspirationPage",
          params: {
            inspirationSeoName: this.inspiration.value?.seoName,
          },
        });
        return;
      }

      this.$router.back();
    },
  },
};
</script>
